import { useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

import {
  ghostButtonSx,
  snippetIconColor,
} from '@modules/_shared/components/feed-v2/constants/styles';
import type { RequalificationChange } from '@modules/_shared/components/feed-v2/types';
import { useLocale } from '@shared/hooks/locale';

import { baseChipSx } from './statusChipStyles';

type Props = {
  changes: RequalificationChange[];
  indented?: boolean;
};

export const RequalificationChanges = ({
  changes,
  indented = false,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLocale();

  if (!changes || changes.length === 0) {
    return null;
  }

  const visibleChanges = expanded ? changes : changes.slice(0, 3);
  const hasMore = changes.length > 3;

  return (
    <Stack spacing={0.5} sx={{ mt: 1, ml: indented ? 2.5 : 0 }}>
      {visibleChanges.map((change, idx) => (
        <Stack
          key={idx}
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {change.field}
          </Typography>
          <Box
            sx={{
              ...baseChipSx,
              bgcolor: 'grey.100',
              fontSize: '0.75rem',
              color: 'text.secondary',
            }}
          >
            {change.from}
          </Box>
          <ArrowRight size={14} color={snippetIconColor} />
          <Box
            sx={{
              ...baseChipSx,
              bgcolor: 'grey.100',
              fontSize: '0.75rem',
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            {change.to}
          </Box>
        </Stack>
      ))}
      {hasMore && (
        <Button
          size="small"
          disableRipple
          onClick={() => setExpanded(!expanded)}
          endIcon={
            expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />
          }
          sx={{
            ...ghostButtonSx,
            mt: 0.25,
            fontSize: '0.75rem',
            alignSelf: 'flex-start',
          }}
        >
          {expanded
            ? t('Show less')
            : t('Show more ({{count}})', { count: changes.length - 3 })}
        </Button>
      )}
    </Stack>
  );
};
